export interface StaticVisualAssetDefinition {
  readonly id: string;
  readonly label: string;
  readonly imagePath: string;
  readonly width?: number;
  readonly height?: number;
  readonly drawScale: number;
  readonly yOffsetTiles: number;
  readonly animated: boolean;
  readonly aliases: readonly string[];
}

export interface ManifestAssetRecord {
  readonly id?: unknown;
  readonly name?: unknown;
  readonly label?: unknown;
  readonly publicPath?: unknown;
  readonly width?: unknown;
  readonly height?: unknown;
  readonly drawScale?: unknown;
  readonly yOffsetTiles?: unknown;
  readonly animated?: unknown;
  readonly aliases?: unknown;
}

export interface ManifestRecord {
  readonly schema?: unknown;
  readonly assets?: readonly ManifestAssetRecord[];
}

const DEFAULT_DRAW_SCALE = 1.25;
const DEFAULT_Y_OFFSET_TILES = 0;
const MANIFEST_URL = `${import.meta.env.BASE_URL}visual-assets/objects/object-assets.json`;

function normalizeAssetRecord(record: ManifestAssetRecord): StaticVisualAssetDefinition | null {
  const id = typeof record.id === 'string'
    ? record.id
    : typeof record.name === 'string'
      ? record.name
      : null;
  const imagePath = typeof record.publicPath === 'string' ? record.publicPath : null;
  if (!id || !imagePath) {
    return null;
  }

  const aliases = Array.isArray(record.aliases)
    ? record.aliases.filter((value): value is string => typeof value === 'string' && value.length > 0)
    : [];

  return {
    id,
    label: typeof record.label === 'string' && record.label.length > 0 ? record.label : humanizeId(id),
    imagePath,
    width: typeof record.width === 'number' && Number.isFinite(record.width) ? record.width : undefined,
    height: typeof record.height === 'number' && Number.isFinite(record.height) ? record.height : undefined,
    drawScale: typeof record.drawScale === 'number' && Number.isFinite(record.drawScale)
      ? record.drawScale
      : DEFAULT_DRAW_SCALE,
    yOffsetTiles: typeof record.yOffsetTiles === 'number' && Number.isFinite(record.yOffsetTiles)
      ? record.yOffsetTiles
      : DEFAULT_Y_OFFSET_TILES,
    animated: record.animated === true,
    aliases,
  };
}

function humanizeId(id: string): string {
  return id
    .replace(/^object_\d+_/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export async function loadObjectAssetManifest(): Promise<ManifestRecord> {
  const manifestResponse = await fetch(MANIFEST_URL, { cache: 'no-cache' });
  const manifest = (await manifestResponse.json()) as ManifestRecord;
  const assets = Array.isArray(manifest.assets) ? manifest.assets : [];
  const animatedCount = assets.reduce((count, asset) => count + (asset?.animated === true ? 1 : 0), 0);
  const animatedIds = assets
    .filter((asset) => asset?.animated === true)
    .map((asset) => String(asset?.id ?? asset?.name ?? 'unknown'));
  console.info(
    `[ObjectAssetManifest] Loaded ${assets.length} assets from ${MANIFEST_URL}. ` +
      `Animated: ${animatedCount}. Animated IDs: ${animatedIds.join(', ') || '(none)'}.`,
  );
  return manifest;
}

const manifest = await loadObjectAssetManifest();

const STATIC_VISUAL_ASSET_DEFINITIONS: readonly StaticVisualAssetDefinition[] = Object.freeze(
  (Array.isArray(manifest.assets) ? manifest.assets : [])
    .map(normalizeAssetRecord)
    .filter((value): value is StaticVisualAssetDefinition => value !== null)
);

const staticVisualAssetDefinitionsById = new Map<string, StaticVisualAssetDefinition>();

for (const definition of STATIC_VISUAL_ASSET_DEFINITIONS) {
  staticVisualAssetDefinitionsById.set(definition.id, definition);
  for (const alias of definition.aliases) {
    if (!staticVisualAssetDefinitionsById.has(alias)) {
      staticVisualAssetDefinitionsById.set(alias, definition);
    }
  }
}

export function getStaticVisualAssetDefinitions(): readonly StaticVisualAssetDefinition[] {
  return STATIC_VISUAL_ASSET_DEFINITIONS;
}

export function getStaticVisualAssetDefinition(assetId: string): StaticVisualAssetDefinition | null {
  return staticVisualAssetDefinitionsById.get(assetId) ?? null;
}
